export type Judge0Language = {
  id: number;
  name: string;
  /** Unique select value (Judge0 language id as string). */
  value: string;
  /** Key used in problem boilerplateCode maps (python, java, etc.). */
  key?: string;
  boilerplate: string;
};

/** Canonical Judge0 ids for course/skill-test language names. */
export const CANONICAL_LANGUAGE_IDS: Record<string, number> = {
  python: 71,
  py: 71,
  javascript: 63,
  js: 63,
  node: 63,
  java: 62,
  cpp: 54,
  "c++": 54,
  c: 48,
  typescript: 74,
  ts: 74,
  go: 60,
  golang: 60,
  rust: 73,
  kotlin: 78,
  swift: 83,
  csharp: 51,
  "c#": 51,
  cs: 51,
  ruby: 72,
  php: 68,
  sql: 82,
  r: 80,
  scala: 81,
  bash: 46,
  perl: 85,
  lua: 64,
  haskell: 61,
};

const LANG_ALIASES: Record<string, string> = {
  "c++": "cpp",
  cplusplus: "cpp",
  js: "javascript",
  node: "javascript",
  py: "python",
  ts: "typescript",
  golang: "go",
  "c#": "csharp",
  cs: "csharp",
};

const BP = {
  python: "# Write your solution here\n",
  javascript: "// Write your solution here\n",
  java: "class Main {\n  public static void main(String[] args) {\n    // Write here\n  }\n}\n",
  cpp: "#include <bits/stdc++.h>\nusing namespace std;\nint main() {\n  return 0;\n}\n",
  c: "#include <stdio.h>\n\nint main() {\n  return 0;\n}\n",
  typescript: "// Write your solution here\n",
  go: "package main\n\nimport \"fmt\"\n\nfunc main() {\n  \n}\n",
  rust: "fn main() {\n  \n}\n",
  kotlin: "fun main() {\n  \n}\n",
  swift: "import Foundation\n\n",
  csharp: "using System;\n\nclass Program {\n  static void Main() {\n    \n  }\n}\n",
  ruby: "# Write your solution here\n",
  php: "<?php\n\n",
  sql: "-- Write your SQL here\n",
  r: "# Write your solution here\n",
  scala: "object Main extends App {\n  \n}\n",
  bash: "#!/bin/bash\n\n",
  perl: "#!/usr/bin/perl\n\n",
  lua: "-- Write your solution here\n",
  haskell: "main :: IO ()\nmain = do\n  putStrLn \"Hello\"\n",
  default: "// Write your code here\n",
};

function lang(
  id: number,
  name: string,
  key?: keyof typeof BP,
  boilerplate?: string,
): Judge0Language {
  return {
    id,
    name,
    value: String(id),
    key,
    boilerplate: boilerplate ?? (key ? BP[key] : BP.default),
  };
}

/** Judge0 languages — aligned with ipx-kk-interviews-web/src/data/languages.js */
export const JUDGE0_LANGUAGES: Judge0Language[] = [
  lang(45, "Assembly (NASM 2.14.02)"),
  lang(46, "Bash (5.0.0)", "bash"),
  lang(47, "Basic (FBC 1.07.1)"),
  lang(75, "C (Clang 7.0.1)", "c"),
  lang(76, "C++ (Clang 7.0.1)", "cpp", "#include <iostream>\nusing namespace std;\nint main() {\n  return 0;\n}\n"),
  lang(48, "C (GCC 7.4.0)", "c"),
  lang(52, "C++ (GCC 7.4.0)", "cpp"),
  lang(49, "C (GCC 8.3.0)", "c"),
  lang(53, "C++ (GCC 8.3.0)", "cpp"),
  lang(50, "C (GCC 9.2.0)", "c"),
  lang(54, "C++ (GCC 9.2.0)", "cpp"),
  lang(86, "Clojure (1.10.1)"),
  lang(51, "C# (Mono 6.6.0.161)", "csharp"),
  lang(77, "COBOL (GnuCOBOL 2.2)"),
  lang(55, "Common Lisp (SBCL 2.0.0)"),
  lang(56, "D (DMD 2.089.1)"),
  lang(57, "Elixir (1.9.4)"),
  lang(58, "Erlang (OTP 22.2)"),
  lang(44, "Executable"),
  lang(87, "F# (.NET Core SDK 3.1.202)"),
  lang(59, "Fortran (GFortran 9.2.0)"),
  lang(60, "Go (1.13.5)", "go"),
  lang(88, "Groovy (3.0.3)"),
  lang(61, "Haskell (GHC 8.8.1)", "haskell"),
  lang(62, "Java (OpenJDK 13.0.1)", "java"),
  lang(63, "JavaScript (Node.js 12.14.0)", "javascript"),
  lang(78, "Kotlin (1.3.70)", "kotlin"),
  lang(64, "Lua (5.3.5)", "lua"),
  lang(79, "Objective-C (Clang 7.0.1)"),
  lang(65, "OCaml (4.09.0)"),
  lang(66, "Octave (5.1.0)"),
  lang(67, "Pascal (FPC 3.0.4)"),
  lang(85, "Perl (5.28.1)", "perl"),
  lang(68, "PHP (7.4.1)", "php"),
  lang(43, "Plain Text", undefined, ""),
  lang(69, "Prolog (GNU Prolog 1.4.5)"),
  lang(70, "Python (2.7.17)", "python", "# Write your solution here\n# Python 2\n"),
  lang(71, "Python (3.8.1)", "python"),
  lang(80, "R (4.0.0)", "r"),
  lang(72, "Ruby (2.7.0)", "ruby"),
  lang(73, "Rust (1.40.0)", "rust"),
  lang(81, "Scala (2.13.2)", "scala"),
  lang(82, "SQL (SQLite 3.27.2)", "sql"),
  lang(83, "Swift (5.2.3)", "swift"),
  lang(74, "TypeScript (3.7.4)", "typescript"),
  lang(84, "Visual Basic.Net (vbnc 0.0.0.5943)"),
];

export const DEFAULT_JUDGE0_LANGUAGE =
  JUDGE0_LANGUAGES.find((l) => l.id === 71) ?? JUDGE0_LANGUAGES[0];

export function getLanguageById(id: number): Judge0Language | undefined {
  return JUDGE0_LANGUAGES.find((l) => l.id === id);
}

export function getBoilerplateKey(language: Judge0Language): string {
  return language.key ?? language.value;
}

export function findLanguageByAlias(alias?: string): Judge0Language | undefined {
  const raw = String(alias || "").toLowerCase().trim();
  if (!raw) return undefined;

  const normalized = LANG_ALIASES[raw] ?? raw;
  const canonicalId = CANONICAL_LANGUAGE_IDS[normalized];
  if (canonicalId) return getLanguageById(canonicalId);

  return JUDGE0_LANGUAGES.find(
    (l) =>
      l.key === normalized ||
      l.value === normalized ||
      l.name.toLowerCase() === normalized,
  );
}
